import { Button, Container, Link } from '@mui/material'
import { FirebaseError, getApp } from 'firebase/app'
import { getFunctions, httpsCallable } from 'firebase/functions'
import { ChangeEvent, useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import ViewTitle from '../../components/ViewTitle'
import StyledInputLabel from '../../components/forms/StyledInputLabel'
import StyledTextField from '../../components/forms/StyledTextField'
import RequiredParameterException from '../../errors/RequiredParameter'
import logger from '../../utils/logger'
import AuthDivFirebase from './components/AuthDivFirebase'
import { StyledFormError } from './components/StyledFormAlert'
import StyledFormButton from './components/StyledFormButton'
import StyledFormHeader from './components/StyledFormHeader'
import StyledFormHelperTypography from './components/StyledFormHelperTypography'
import { StyledFormLink } from './components/StyledFormLink'
import StyledFormSubheader from './components/StyledFormSubheader'
import { ScreenPhase } from './components/types'

type Field = 'email'

interface Fields {
  email?: string
}

interface Alerts extends Fields {
  success?: string
  error?: string
}

const VALID_EMAIL_ACTIONS = ['link', 'login', 'resetPassword', 'recoverAccount'] as const
type EmailAction = typeof VALID_EMAIL_ACTIONS[number]

interface FirebaseEmailRequest {
  action: EmailAction
  identifier: string
  url: string
}

interface FirebaseEmailResponse {
  message?: string
  error?: FirebaseError
}

const emailPattern = /^[a-zA-Z0-9_.+-]+@[a-zA-Z0-9-]+\.[a-zA-Z0-9-.]+$/
const isEmail = (email: string) => emailPattern.test(email)

/**
 * Renders Forgot password form
 */

export default function ForgotPasswordFirebase() {
  // fields button, & screen state
  const [fields, setFields] = useState<Fields>({})
  const [alerts, setAlerts] = useState<Alerts>({})
  const [screenPhase, setScreenPhase] = useState(ScreenPhase.FORM)
  const sendResetPasswordButton = useRef<null | HTMLButtonElement>(null)
  const [isAttemptingToSend, setIsAttemptingToSend] = useState(false)

  // styling & browser url params
  const location = useLocation()

  // firebase initialization
  const app = getApp()
  const functions = getFunctions(app)

  const enterKeyListener = (event: React.KeyboardEvent<HTMLDivElement>) => {
    event.preventDefault()
    if (event.key === 'Enter') {
      sendResetPasswordButton.current?.focus()
    }
  }

  // much faster than doing a hard reload
  const resetState = () => {
    setFields({})
    setAlerts({})
    setScreenPhase(ScreenPhase.FORM)
    setIsAttemptingToSend(false)
  }

  // Handle textfield change
  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const updatedFields = { ...fields }
    const field = event.target.name as Field
    updatedFields[field] = event.target.value
    setFields(updatedFields)

    const updatedErrors = { ...alerts }
    delete updatedErrors[field]
    delete updatedErrors.error
    setAlerts(updatedErrors)
    setIsAttemptingToSend(false)
  }

  const sendLink = async (action: 'login' | 'resetPassword') => {
    // prevents spam clicking
    if (isAttemptingToSend) {
      return
    }

    try {
      const { email: username } = fields
      if (!username) {
        throw new RequiredParameterException('Email is required', 'email')
      }

      setIsAttemptingToSend(true)

      const addMessage = httpsCallable<FirebaseEmailRequest, FirebaseEmailResponse>(functions, 'firebaseEmail')
      const { data } = await addMessage({ identifier: username, action, url: window.location.href })

      const { error } = data
      if (error) {
        throw error
      }

      if (isEmail(username)) {
        localStorage.setItem('_pb_linkSignInEmail', username)
      }
      setScreenPhase(ScreenPhase.EMAIL_SENT)
    } catch (err: any) {
      let error
      if (err.errorInfo) error = err.errorInfo
      else error = err as RequiredParameterException
      const emailName = action === 'login' ? 'magic link' : 'password reset'
      logger.error(err)

      switch (error.code) {
        case 'RequiredParameterException':
          setAlerts({ [error.parameterName]: error.message })
          break
        case 'auth/too-many-requests':
        case 'auth/invalid-email':
        case 'auth/invalid-identifier':
        case 'auth/user-not-found':
        case 'auth/import-failed':
          setScreenPhase(ScreenPhase.EMAIL_SENT)
          break
        default:
          setIsAttemptingToSend(false)
          setAlerts({ error: `There was an error sending the ${emailName} email. Please resubmit your username or email address.` })
      }
    }
  }

  const sendMagicLink = () => sendLink('login')
  const sendResetPasswordLink = () => sendLink('resetPassword')

  useEffect(() => {
    const savedEmail = localStorage.getItem('_pb_linkSignInEmail')
    if (savedEmail && !isEmail(savedEmail)) {
      localStorage.removeItem('_pb_linkSignInEmail')
    }
  }, [])

  return (
    <AuthDivFirebase>
      <ViewTitle title="Forgot Password" />

      {/* Renders the form to send an email, what the user initially sees */}
      {screenPhase === ScreenPhase.FORM && (
        <>
          <StyledFormHeader sx={{ marginBottom: '4px !important' }}>Forgot your password?</StyledFormHeader>
          <StyledFormSubheader sx={{ mb: 2 }}>Enter the email on your account and we will send you a password reset link.</StyledFormSubheader>

          {/* Error alert */}
          <StyledFormError title="Failed to send link" body={alerts.error} />

          <StyledInputLabel disabled={isAttemptingToSend} htmlFor="username">
            Email/Username
          </StyledInputLabel>
          <StyledTextField
            id="username"
            autoFocus
            data-test="username"
            disabled={isAttemptingToSend}
            error={Boolean(alerts.email)}
            inputProps={{ autoCapitalize: 'off' }}
            fullWidth
            helperText={alerts.email}
            margin="dense"
            name="email"
            onKeyUp={enterKeyListener}
            onChange={handleChange}
            required
            placeholder="name@gmail.com"
            type="email"
            variant="outlined"
          />
          <StyledFormButton
            ref={sendResetPasswordButton}
            name="resetPassword"
            data-test="submit"
            disabled={isAttemptingToSend}
            fullWidth
            sx={{ mb: 3 }}
            variant="contained"
            onClick={sendResetPasswordLink}
          >
            Reset Password
          </StyledFormButton>

          {/* Magic Link - active */}
          <Button id="loginLink" data-testid="send-link" onClick={sendMagicLink} variant="text" disabled={isAttemptingToSend}>
            Send me a magic link
          </Button>

          {/* Acts as a spacer between the links. style and sx props don't work on the Link tag */}
          <Container maxWidth={false} sx={{ my: 2 }} />

          <StyledFormLink data-test="return-to-login" to={`/auth/login${location.search}`}>
            Return to Login
          </StyledFormLink>
        </>
      )}

      {/* Renders the Email Sent screen, after an email may or may not have been sent */}
      {screenPhase === ScreenPhase.EMAIL_SENT && (
        <>
          <StyledFormHeader sx={{ marginBottom: '4px !important' }}>Email Sent</StyledFormHeader>
          <StyledFormSubheader sx={{ textOverflow: 'ellipsis', whiteSpace: 'break-spaces', overflow: 'hidden' }}>
            {
              fields.email?.includes('@')
                ? `We've sent an email to ${fields.email}.` // input was email address
                : `We've sent an email to the address associated with ${fields.email}.` // input was username
            }
            <br />
          </StyledFormSubheader>
          <StyledFormButton
            ref={sendResetPasswordButton}
            name="forgot-password-try-again"
            color="primary"
            fullWidth
            sx={{ my: 3 }}
            variant="contained"
            onClick={resetState}
          >
            Send another email
          </StyledFormButton>
          <StyledFormHelperTypography>
            Having trouble?
            <Link
              underline="none"
              sx={{ my: 2, fontSize: '14px' }}
              target="_blank"
              href="https://support.photobucket.com/hc/en-us/categories/4404271347732-Account-Recovery"
            >
              {' '}
              Visit help center
            </Link>
          </StyledFormHelperTypography>
        </>
      )}
    </AuthDivFirebase>
  )
}
